import React, {createContext, ReactNode, useEffect, useMemo} from 'react';

import axios, { AxiosInstance } from "axios";
import { QueryClient } from "react-query";
import { LS } from "../utils";
import {LS_KEYS, ROUTES} from "../types";
import {log} from "util";
import {da} from "date-fns/locale";

const {clearLS, getFromLS, removeFromLS, setToLS} = LS;

interface IApiTye {
  client: AxiosInstance;
}

export const axiosApiInstance = axios.create({
  baseURL: `${process.env.REACT_APP_BASE_URL}/api`,
  headers: { Accept: 'application/json', "Content-Type": "application/json" },
});

const Api = createContext<IApiTye | null>(null);

const refreshToken = () => {
  const data = {
    refresh: !!getFromLS(LS_KEYS.REFRESH_TOKEN) ? JSON.parse(getFromLS(LS_KEYS.REFRESH_TOKEN) || '') : ''
  }
  return axiosApiInstance.post(`/auth/refresh/`, data);
}

const Provider = ({
  queryClient,
  children
}: {
  queryClient: QueryClient;
  children: ReactNode
}) => {
  queryClient.setQueryDefaults("calculatorData", {
    queryFn: async () => {
      const { data } = await axiosApiInstance.get('/public/calculator/');
      return data;
    }
  })

  queryClient.setQueryDefaults("userInfo", {
    queryFn: async () => {
      const { data } = await axiosApiInstance.get('/users/me/');
      return data;
    }
  })

  queryClient.setQueryDefaults("selectProducts", {
    queryFn: async () => {
      const { data } = await axiosApiInstance.get('/public/products');
      return data;
    }
  })

  queryClient.setQueryDefaults("statisticsProducts", {
    queryFn: async () => {
      const { data } = await axiosApiInstance.get('/statistics/info/types');
      return data;
    }
  })

  queryClient.setQueryDefaults("activateEmail", {
    queryFn: async ({ queryKey }) => {
      const [, uid, token] = queryKey;
      const { data } = await axiosApiInstance.get(`/users/activate/${uid}/${token}/`);
      return data;
    }
  })

  queryClient.setQueryDefaults("statisticsBanks", {
    queryFn: async () => {
      const { data } = await axiosApiInstance.get('/statistics/info/banks');
      return data;
    }
  })

  queryClient.setQueryDefaults("publicProductsList", {
    queryFn: async ({ queryKey }) => {
      const [, type] = queryKey;
      const url = new URL(`${process.env.REACT_APP_BASE_URL}/api/public/products/`);
      type && url.searchParams.append("type", `${type}`);

      const { data } = await axiosApiInstance.get(url?.href);
      return data;
    }
  })

  queryClient.setQueryDefaults("productsList", {
    queryFn: async ({ queryKey }) => {
      const [, type] = queryKey;
      const url = new URL(`${process.env.REACT_APP_BASE_URL}/api/products/list`);
      type && url.searchParams.append("type", `${type}`);

      const { data } = await axiosApiInstance.get(url?.href);
      return data;
    }
  })

  queryClient.setQueryDefaults("productsFormFields", {
    queryFn: async ({ queryKey }) => {
      const [, id] = queryKey;
      const { data } = await axiosApiInstance.get(`/products/${id}`);
      return data;
    }
  })

  queryClient.setQueryDefaults("productsFilters", {
    queryFn: async () => {
      const { data } = await axiosApiInstance.get('/products/info/types');
      return data;
    }
  })

  queryClient.setQueryDefaults("publicProductsFilters", {
    queryFn: async () => {
      const { data } = await axiosApiInstance.get('/public/products/types');
      return data;
    }
  })

  queryClient.setQueryDefaults("productFormList", {
    queryFn: async () => {
      const { data } = await axiosApiInstance.get('/products/form/list');
      return data;
    }
  })

  queryClient.setQueryDefaults("statisticsOrders", {
    queryFn: async ({ queryKey }) => {
      const [, date_min, date_max, page, max_on_page, product, bank] = queryKey;

      const url = new URL(`${process.env.REACT_APP_BASE_URL}/api/statistics/orders`);
      date_max && url.searchParams.append('date_max', `${date_max}`);
      date_min && url.searchParams.append('date_min', `${date_min}`);
      page && url.searchParams.append('page', `${page}`);
      max_on_page && url.searchParams.append('page_size', `${max_on_page}`);
      product && url.searchParams.append('type', `${product}`);
      bank && url.searchParams.append('bank', `${bank}`);

      const { data } = await axiosApiInstance.get(url?.href);
      return data;
    }
  })

  queryClient.setQueryDefaults("downloadTable", {
    queryFn: async ({ queryKey }) => {
      const [, date_max, date_min] = queryKey;

      const url = new URL(`${process.env.REACT_APP_BASE_URL}/api/statistics/orders/export`);
      date_max && url.searchParams.append('date_max', `${date_max}`);
      date_min && url.searchParams.append('date_min', `${date_min}`);

      const { data } = await axiosApiInstance.get(url?.href);
      return data;
    }
  })

  queryClient.setMutationDefaults("signUp", {
    mutationFn: (data: { username: string; fio: string; email: string; password: string; phone_number: string }) => {
      return axiosApiInstance.post("/auth/register/", data);
    },
  })

  queryClient.setMutationDefaults("getFormFields", {
    mutationFn: (data: { bank: string | number; type: string }) => {
      return axiosApiInstance.post("/products/form/get_fields/", data);
    }
  })

  queryClient.setMutationDefaults("submitForm", {
    mutationFn: (data: {
      data: {
        data: Array<{ name: string; value: string }>
      },
      id: string | number
    }) => {
      return axiosApiInstance.post(`/products/submit/${data.id}`, { data: data.data });
    }
  })

  queryClient.setMutationDefaults("signIn", {
    mutationFn: (data: { username: string; password: string }) => {
      return axiosApiInstance.post("/auth/token/", data);
    },
  })

  queryClient.setMutationDefaults("updateUserInfo", {
    mutationFn: (data: {
      username?: string;
      fio?: string;
      email?: string;
      phone_number?: string;
      password?: string;
    }) => {
      return axiosApiInstance.put("/users/me/", data)
    }
  })

  queryClient.setMutationDefaults("sendRequest", {
    mutationFn: (data: { fio?: string; phone?: string; email?: string }) => {
      return axiosApiInstance.post("/public/form/", data)
    }
  })

  queryClient.setMutationDefaults("resetEmail", {
    mutationFn: (data: { email: string; }) => {
      return axiosApiInstance.post("/users/reset/", data)
    }
  })

  queryClient.setMutationDefaults("resetValidateToken", {
    mutationFn: (data: { token: string; }) => {
      return axiosApiInstance.post("/users/reset/validate_token/", data)
    }
  })

  queryClient.setMutationDefaults("setNewPassword", {
    mutationFn: (data: { password: string; token: string; }) => {
      return axiosApiInstance.post("/users/reset/confirm/", data)
    }
  })

  queryClient.setMutationDefaults("sendUserForm", {
    mutationFn: (data: { form: any }) => {
      return axiosApiInstance.post("/users/me/form/", data, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      })
    }
  })

  useEffect(() => {
    axiosApiInstance.interceptors.request.use( (config) => {
      let token = getFromLS(LS_KEYS.ACCESS_TOKEN) || '';
      token = !!token ? JSON.parse(token) : '';
      config.headers.Authorization = 'Bearer ' + token;
      return config;
    }, async (error) => {
      return Promise.reject(error);
    });

    axiosApiInstance.interceptors.response.use( (response) => {
      return response;
    }, async (error) => {
      if (error.response.status === 401 && error.response.data.code === 'token_not_valid') {
        await refreshToken()
          .then(({ data: { access }}) => {
            removeFromLS(LS_KEYS.ACCESS_TOKEN);
            setToLS(LS_KEYS.ACCESS_TOKEN, JSON.stringify(`${access}`))
          })
          .catch(() => {
            clearLS();
            window.location.href = ROUTES.SIGN_IN;
            return Promise.reject(error)
          })
      }

      return Promise.reject(error);
    });
  }, []);

  return (
    <Api.Provider
      value={useMemo(() => ({
        client: axiosApiInstance,
      }), [])}
    >
      {children}
    </Api.Provider>
  )
}


export default React.memo(Provider);
